<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

// Services
import companyNeedService from "@/modules/companyNeed/services/companyNeed";

// Types
import type { CompanyNeedActionProgress, CompanyNeedItem, CompanyNeedModal } from "@/modules/companyNeed/types/modals";
import type { MoveWorker } from "@/modules/companyNeed/types/moveWorker";

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import { DropdownDirections } from "@/dsComponents/Dropdown/types";
// Components
import Loader from "@/dsComponents/Loader.vue";
import QuickActionItem from "@/dsComponents/QuickActions/QuickActionItem.vue";

interface Props {
    workers: MoveWorker[];
    companyNeedId?: string;
    search?: string;
    selectedIndex?: number | null;
    showAction?: boolean;
    actionProgress?: CompanyNeedActionProgress;
}

const props = withDefaults(defineProps<Props>(), {
    search: "",
    selectedIndex: null,
    showAction: false,
    actionProgress: () => ({
        alreadyCompanyNeedIds: [],
        inProgressCompanyNeedId: null,
    }),
});

const { t } = useI18n();

const emit = defineEmits<{
    (e: "select", need: CompanyNeedItem): void;
    (e: "add", need: CompanyNeedItem): void;
    (e: "update:itemCount", count: number): void;
}>();

const loading = ref(false);
const companyNeeds = ref<CompanyNeedModal[]>([]);

const selectedNeedId = ref<string | null>(null);

const allCompanyNeeds = computed(() => {
    return companyNeeds.value.flatMap((group: CompanyNeedModal) => group.needs);
});

watch(
    () => allCompanyNeeds.value.length,
    (count) => emit("update:itemCount", count),
    { immediate: true }
);

defineExpose({
    executeSelected: (index: number) => {
        const need = allCompanyNeeds.value[index];
        if (need) {
            handleSelectNeed(need);
        }
    },
});

const fetchCompanyNeeds = async () => {
    try {
        loading.value = true;
        companyNeeds.value = await companyNeedService.fetchCompanyNeedsForModal(props.companyNeedId, props.search, props.workers);
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const hasCompletedAction = (companyNeedId: string): boolean =>
    props.actionProgress.alreadyCompanyNeedIds.includes(companyNeedId);

const isActionInProgress = (companyNeedId: string): boolean =>
    props.actionProgress.inProgressCompanyNeedId === companyNeedId;

const getDisplayedPoolCount = (need: CompanyNeedItem): number =>
    hasCompletedAction(need.id) ? need.poolCount + props.workers.length : need.poolCount;

const getActionButtonProps = (need: CompanyNeedItem) => {
    const isCompleted = hasCompletedAction(need.id);

    return {
        variant: isCompleted ? "ghost" : "stroke",
        iconLeft: isCompleted ? "check" : undefined,
        label: isCompleted ? t("modals.companyNeed.moveWorker.addToOtherPools.added") : t("global.actions.add"),
        placeholder: isCompleted,
        noHover: isCompleted,
        readonly: isCompleted,
        loadingLeft: isActionInProgress(need.id),
    } as const;
};

const handleAddToCompanyNeedPool = (need: CompanyNeedItem) => {
    if (hasCompletedAction(need.id) || props.actionProgress.inProgressCompanyNeedId) return;
    emit("add", need);
};

const handleSelectNeed = (need: CompanyNeedItem) => {
    if (props.showAction) {
        handleAddToCompanyNeedPool(need);
        return;
    }
    selectedNeedId.value = null;
    emit("select", need);
};

const getGlobalIndex = (companyNeedGroup: CompanyNeedModal, needIndex: number): number => {
    let index = 0;
    for (const group of companyNeeds.value) {
        if (group.companyId === companyNeedGroup.companyId) {
            return index + needIndex;
        }
        index += group.needs.length;
    }
    return -1;
};

onBeforeMount(() => {
    fetchCompanyNeeds();
});

watch(
    () => props.search,
    () => {
        fetchCompanyNeeds();
    }
);
</script>

<template>
    <template v-if="loading">
        <div class="company-needs-quick-action-item__loading">
            <Loader />
        </div>
    </template>
    <template v-else-if="companyNeeds.length === 0">
        <div class="company-needs-quick-action-item__empty typo-xs-regular">
            {{ t("global.noResults") }}
        </div>
    </template>
    <template v-else v-for="companyNeedGroup in companyNeeds" :key="companyNeedGroup.companyId">
        <div class="company-needs-quick-action-item__title">
            <span class="company-needs-quick-action-item__title-label typo-xxs-medium">{{ companyNeedGroup.companyName }}</span>
        </div>
        <QuickActionItem
            v-for="(need, needIndex) in companyNeedGroup.needs"
            :selected="props.selectedIndex === getGlobalIndex(companyNeedGroup, needIndex)"
            :key="need.id"
            @click="handleSelectNeed(need)"
        >
            <template #infos>
                <div class="company-needs-quick-action-item__infos">
                    <i
                        v-if="!showAction"
                        class="ti ti-folder-open typo-md-regular company-needs-quick-action-item__infos-icon"
                    />
                    <span class="company-needs-quick-action-item__infos-label typo-xs-medium">
                        {{ need.name }}
                    </span>
                </div>
            </template>
            <template #more-infos>
                <div class="company-needs-quick-action-item__more-infos">
                    <span class="company-needs-quick-action-item__more-infos-label typo-xs-regular">{{ need.job }}</span>
                </div>
            </template>
            <template #first-details v-if="need.orderCount > 0">
                <Dropdown mode="hover" width="auto" :direction="DropdownDirections.TOP_CENTER">
                    <template #trigger-zone>
                        <ComboButton iconLeft="order" :label="String(need.orderCount)" forceDefaultStyle />
                    </template>
                    <template #dropdown-content>
                        <span class="typo-xxs-regular">{{ t("page.companyNeed.layout.tabs.orders") }}</span>
                    </template>
                </Dropdown>
            </template>
            <template #second-details v-if="getDisplayedPoolCount(need) > 0">
                <Dropdown mode="hover" width="auto" :direction="DropdownDirections.TOP_CENTER">
                    <template #trigger-zone>
                        <ComboButton variant="ghost" iconLeft="star" :label="String(getDisplayedPoolCount(need))" forceDefaultStyle />
                    </template>
                    <template #dropdown-content>
                        <span class="typo-xxs-regular">{{ t("page.companyNeed.layout.tabs.pool") }}</span>
                    </template>
                </Dropdown>
            </template>
            <template v-if="showAction" #actions>
                <ComboButton v-bind="getActionButtonProps(need)" @click.stop="handleAddToCompanyNeedPool(need)" />
            </template>
        </QuickActionItem>
    </template>
</template>

<style scoped lang="scss">
.company-needs-quick-action-item {
    width: 100%;
    cursor: pointer;

    &__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        gap: var(--spacing-xs, 4px);
    }

    &__empty {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: var(--spacing-md, 8px);
        color: var(--text-sub-600, #494e61);
        user-select: none;
    }

    &__title {
        display: flex;
        height: 44px;
        padding: var(--spacing-lg, 12px) 14px var(--spacing-sm, 6px) var(--spacing-md, 8px);
        align-items: center;
        gap: var(--spacing-xs, 4px);
        flex: 1 0 0;
        width: 100%;
        user-select: none;

        &-label {
            color: var(--text-sub-600, #494e61);
        }
    }

    &__infos {
        display: flex;
        align-items: center;
        gap: var(--spacing-lg, 12px);
        user-select: none;
        min-width: 0;

        &-icon {
            color: var(--text-sub-600, #494e61);
        }

        &-label {
            color: var(--icon-sub-500, #636778);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__more-infos {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs, 4px);
        color: var(--text-sub-600, #494e61);
        min-width: 0;

        &-label {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
}

.search-result-item {
    &:hover {
        .company-needs-quick-action-item__infos-label,
        .company-needs-quick-action-item__more-infos-label {
            color: var(--text-main-900, #00061a);
        }
    }

    &--selected {
        .company-needs-quick-action-item__infos-label,
        .company-needs-quick-action-item__more-infos-label {
            color: var(--text-main-900, #00061a);
        }
    }
}
</style>
